import React, { useCallback, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    AttachmentKey,
    ConversationMessageKey,
} from 'flow/types/TicketEventData';
import { TicketKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import {
    selectEventDirection,
    selectEventTimestamp,
    selectMessageDeliveryStatusIsSending,
} from 'flow/selectors/TicketEventSelectors';
import useAppContext from 'flow/hooks/useAppContext';
import MessageItemSent from 'flow/components/message/MessageItemSent';
import MessageSender from 'flow/components/tickets/MessageSender';
import SenderAvatarExternalService from 'flow/components/tickets/event-item/Messages/SenderAvatarExternalService';
import AttachmentsContainer from 'flow/components/tickets/event-item/Messages/AttachmentsContainer';
import DeliveryStatus from 'flow/components/tickets/event-item/Messages/DeliveryStatus';
import EmailBody from 'flow/components/tickets/event-item/Email/EmailBody';
import ButtonShowSenderAndRecipients from 'flow/components/tickets/event-item/Email/ButtonShowSenderAndRecipients';

interface Props {
    ticketKey: TicketKey;
    messageKey: ConversationMessageKey;
    onNewAttachmentsFetched?: () => void;
}

const EventEmailMessageV2 = ({
    ticketKey,
    messageKey,
    onNewAttachmentsFetched,
}: Props) => {
    const { i18next } = useAppContext();

    const direction = useAppSelector((state) =>
        selectEventDirection(state, ticketKey, messageKey),
    );
    const timestamp = useAppSelector((state) =>
        selectEventTimestamp(state, ticketKey, messageKey),
    );
    const deliveryStatusIsSending = useAppSelector((state) =>
        selectMessageDeliveryStatusIsSending(state, ticketKey, messageKey),
    );
    const [attachmentsInlined, setAttachmentsInlined] =
        useState<Set<AttachmentKey>>(undefined);
    const attachmentsInlineParsingFinished = attachmentsInlined !== undefined;

    const onAttachmentsInlined = useCallback((inlined: Set<AttachmentKey>) => {
        setAttachmentsInlined(inlined);
    }, []);

    return (
        <div
            className={twMerge(
                'bg-gray-150 flex flex-col gap-1 rounded-xl p-3',
                deliveryStatusIsSending && 'animate-fade opacity-50',
            )}
        >
            <div className="flex h-6 items-center gap-2 text-sm">
                <SenderAvatarExternalService
                    ticketKey={ticketKey}
                    messageKey={messageKey}
                    size="small"
                />
                <div className="flex min-w-0 items-center gap-0.5">
                    <MessageSender
                        ticketKey={ticketKey}
                        messageKey={messageKey}
                    />
                    {direction === 'INCOMING' && (
                        <ButtonShowSenderAndRecipients
                            ticketKey={ticketKey}
                            eventKey={messageKey}
                        />
                    )}
                </div>
                <span className="text-sm text-gray-500">
                    {i18next.t('ticket.email.label')}
                </span>
                <span className="ms-auto text-sm text-gray-500">
                    <MessageItemSent timeString={timestamp} />
                </span>
            </div>
            <div className="ps-8 wrap-break-word whitespace-pre-wrap">
                <div className="min-w-0 text-base wrap-break-word text-gray-800 no-underline">
                    <EmailBody
                        ticketKey={ticketKey}
                        eventKey={messageKey}
                        sentTime={timestamp}
                        onAttachmentsInlined={onAttachmentsInlined}
                    />
                    {attachmentsInlineParsingFinished && (
                        <AttachmentsContainer
                            ticketKey={ticketKey}
                            eventKey={messageKey}
                            onNewAttachmentsFetched={onNewAttachmentsFetched}
                            isBeingSent={deliveryStatusIsSending}
                            attachmentsToExclude={attachmentsInlined}
                        />
                    )}
                </div>
                {direction === 'OUTGOING' && (
                    <DeliveryStatus
                        messageKey={messageKey}
                        ticketKey={ticketKey}
                        displayFailedAndSendingOnly={true}
                    />
                )}
            </div>
        </div>
    );
};

export default EventEmailMessageV2;
